import { Col, Row } from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  BankOutlined,
  DollarOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { KpiCard, MoneyText } from '@/shared/components/ui';
import { reportsApi } from '@/api/reports';
import { financeApi } from '@/api/finance';

/**
 * Cashflow rail rendered above the Finance hub tile grid. Pulls existing
 * endpoints — no new backend — and gives the accountant the four numbers
 * they want before clicking into any sub-page:
 *
 *   Net today          — fee receipts today, net of reversals
 *   Income MTD         — every income-class GL credit since the 1st
 *   Expenses MTD       — every expense-class GL debit since the 1st
 *   Net surplus MTD    — Income − Expenses, color-coded
 *
 * All four are sensitive so the eye toggle hides them between meetings.
 * The MTD card carries an explicit up/down trend arrow on top of the tone
 * (green/red) since color alone is a weak signal for surplus/deficit.
 */
export function FinanceMonthRail() {
  const today = dayjs().format('YYYY-MM-DD');
  const monthStart = dayjs().startOf('month').format('YYYY-MM-DD');

  const feeToday = useQuery({
    queryKey: ['reports', 'fee-collection', today, today, 'finance-rail'],
    queryFn: () => reportsApi.feeCollection(today, today),
    staleTime: 60 * 1000,
  });

  const mtd = useQuery({
    queryKey: ['finance', 'income-expenditure', monthStart, today, 'finance-rail'],
    queryFn: () => financeApi.incomeExpenditure(monthStart, today),
    staleTime: 60 * 1000,
  });

  const netToday = feeToday.data ? Number(feeToday.data.netAmount) : 0;
  const income = mtd.data ? Number(mtd.data.totalIncome) : 0;
  const expense = mtd.data ? Number(mtd.data.totalExpense) : 0;
  const surplus = mtd.data ? Number(mtd.data.netSurplus) : 0;
  const surplusUp = surplus >= 0;

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} sm={12} xl={6}>
        <KpiCard
          label="Fees collected today"
          value={<MoneyText value={netToday} strong />}
          hint="Net of receipt reversals"
          icon={<DollarOutlined />}
          tone="success"
          sensitive
        />
      </Col>
      <Col xs={24} sm={12} xl={6}>
        <KpiCard
          label="Income (month-to-date)"
          value={<MoneyText value={income} strong />}
          hint={`Since ${dayjs(monthStart).format('D MMM')}`}
          icon={<ArrowUpOutlined />}
          tone="info"
          sensitive
        />
      </Col>
      <Col xs={24} sm={12} xl={6}>
        <KpiCard
          label="Expenses (month-to-date)"
          value={<MoneyText value={expense} strong />}
          hint="Operating costs since the 1st"
          icon={<ArrowDownOutlined />}
          tone="warning"
          sensitive
        />
      </Col>
      <Col xs={24} sm={12} xl={6}>
        <KpiCard
          label="Net surplus (month-to-date)"
          value={
            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
              {surplusUp
                ? <ArrowUpOutlined style={{ fontSize: 14, color: 'var(--ant-color-success)' }} />
                : <ArrowDownOutlined style={{ fontSize: 14, color: 'var(--ant-color-error)' }} />}
              <MoneyText value={surplus} strong />
            </span>
          }
          hint={surplusUp ? "Operating in surplus" : "Spending exceeds income — review expenses"}
          icon={surplusUp ? <BankOutlined /> : <WalletOutlined />}
          tone={surplusUp ? 'success' : 'danger'}
          sensitive
        />
      </Col>
    </Row>
  );
}
